import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

const Device = () => {
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();

  const [deviceStats] = useState({
    motors: 24,
    reducers: 18,
    rollers: 12,
    others: 108
  });

  const [devices] = useState([
    {
      id: 1,
      name: '主电机 #1',
      model: 'Y2-280M-4',
      power: '90kW',
      status: 'online',
      runtime: '2,456h'
    },
    {
      id: 2,
      name: '减速机 #2',
      model: 'ZQ-650',
      ratio: '31.5',
      status: 'warning',
      runtime: '1,892h'
    },
    {
      id: 3,
      name: '驱动滚筒 #3',
      model: 'DT-800',
      diameter: '800mm',
      status: 'online',
      runtime: '3,124h'
    }
  ]);

  const [sensors] = useState([
    {
      device: '主电机 #1',
      count: 6,
      types: [
        { name: '温度传感器', count: 2, icon: 'thermometer-half', color: 'red' },
        { name: '振动传感器', count: 2, icon: 'wave-square', color: 'blue' },
        { name: '电流传感器', count: 1, icon: 'bolt', color: 'yellow' },
        { name: '转速传感器', count: 1, icon: 'tachometer-alt', color: 'green' }
      ]
    },
    {
      device: '减速机 #2',
      count: 4,
      types: [
        { name: '温度传感器', count: 2, icon: 'thermometer-half', color: 'red' },
        { name: '振动传感器', count: 1, icon: 'wave-square', color: 'blue' },
        { name: '油温传感器', count: 1, icon: 'tint', color: 'purple' }
      ]
    }
  ]);

  const [systemConfig] = useState([
    { name: '数据采集频率', value: '1秒', icon: 'clock', color: 'blue' },
    { name: '数据存储周期', value: '2年', icon: 'database', color: 'green' },
    { name: '报警阈值设置', value: '已配置', icon: 'bell', color: 'orange' },
    { name: '自动备份设置', value: '每日', icon: 'sync', color: 'purple' }
  ]);

  const [maintenanceRecords] = useState([
    {
      title: '主电机轴承检查',
      date: '2025-08-29',
      operator: '董工',
      status: 'completed'
    },
    {
      title: '减速机换油',
      date: '2025-08-29',
      operator: '董工',
      status: 'completed'
    },
    {
      title: '驱动滚筒检查',
      date: '2025-08-29',
      operator: '董工',
      status: 'in-progress'
    }
  ]);

  const handleAddDevice = () => {
    showConfirm('添加设备', '确定要添加新设备吗？', () => {
      showToast('正在打开设备添加向导...');
    });
  };

  const handleQuickAction = (action) => {
    showToast(`正在打开${action}...`);
  };

  const handleConfigItem = (item) => {
    showToast(`正在配置${item.name}...`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'text-success-600';
      case 'warning': return 'text-warning-600';
      case 'offline': return 'text-danger-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'online': return 'bg-success-50 border-success-200';
      case 'warning': return 'bg-warning-50 border-warning-200';
      case 'offline': return 'bg-danger-50 border-danger-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="pb-4">
      {/* 顶部状态栏 */}
      <div className="phm-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">设备管理</h1>
            <p className="text-sm opacity-90">设备配置 · 传感器管理</p>
          </div>
          <div className="text-right">
            <div className="text-xs opacity-80">设备总数</div>
            <div className="text-sm font-semibold">162台</div>
          </div>
        </div>
      </div>

      {/* 设备分类统计 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">设备分类统计</h3>
        <div className="phm-metric-grid">
          <div className="phm-metric-item">
            <div className="phm-metric-value text-primary-600">{deviceStats.motors}</div>
            <div className="phm-metric-label">电机设备</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-success-600">{deviceStats.reducers}</div>
            <div className="phm-metric-label">减速机</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-purple-600">{deviceStats.rollers}</div>
            <div className="phm-metric-label">驱动滚筒</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-orange-600">{deviceStats.others}</div>
            <div className="phm-metric-label">其他设备</div>
          </div>
        </div>
      </div>

      {/* 设备列表 */}
      <div className="phm-status-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">设备列表</h3>
          <button 
            className="px-3 py-1 bg-primary-500 text-white text-xs rounded-lg hover:bg-primary-600 transition-colors"
            onClick={handleAddDevice}
          >
            <i className="fas fa-plus mr-1"></i>添加设备
          </button>
        </div>
        <div className="space-y-3">
          {devices.map((device) => (
            <div key={device.id} className={`flex items-center justify-between p-3 rounded-lg border ${getStatusBg(device.status)}`}>
              <div className="flex items-center space-x-3">
                <i className="fas fa-cog text-primary-600"></i>
                <div>
                  <div className="font-medium text-gray-800">{device.name}</div>
                  <div className="text-sm text-gray-500">
                    {device.model} | {device.power || `减速比: ${device.ratio}` || `直径: ${device.diameter}`}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${getStatusColor(device.status)}`}>
                  {device.status === 'online' ? '在线' : device.status === 'warning' ? '预警' : '离线'}
                </div>
                <div className="text-xs text-gray-500">运行时间: {device.runtime}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 传感器配置 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">传感器配置</h3>
        <div className="space-y-3">
          {sensors.map((sensor, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-800">{sensor.device} 传感器</span>
                <span className="text-xs text-gray-500">{sensor.count}个传感器</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {sensor.types.map((type, typeIndex) => (
                  <div key={typeIndex} className="flex items-center space-x-2">
                    <i className={`fas fa-${type.icon} text-${type.color}-500`}></i>
                    <span>{type.name} x{type.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 系统配置 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">系统配置</h3>
        <div className="space-y-3">
          {systemConfig.map((config, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => handleConfigItem(config)}
            >
              <div className="flex items-center space-x-3">
                <i className={`fas fa-${config.icon} text-${config.color}-600`}></i>
                <span className="text-sm text-gray-700">{config.name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-800">{config.value}</span>
                <i className="fas fa-chevron-right text-gray-400"></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 维护记录 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">最近维护记录</h3>
        <div className="space-y-2">
          {maintenanceRecords.map((record, index) => (
            <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
              <i className="fas fa-tools text-primary-600"></i>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800">{record.title}</div>
                <div className="text-xs text-gray-500">{record.date} | 维护人员: {record.operator}</div>
              </div>
              <span className={`text-xs ${record.status === 'completed' ? 'text-success-600' : 'text-warning-600'}`}>
                {record.status === 'completed' ? '已完成' : '进行中'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 快速操作 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">快速操作</h3>
        <div className="grid grid-cols-2 gap-3">
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-primary-50 text-primary-700 rounded-lg border border-primary-200 hover:bg-primary-100 transition-colors"
            onClick={() => handleQuickAction('添加设备')}
          >
            <i className="fas fa-plus"></i>
            <span className="text-sm font-medium">添加设备</span>
          </button>
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-success-50 text-success-700 rounded-lg border border-success-200 hover:bg-success-100 transition-colors"
            onClick={() => handleQuickAction('系统设置')}
          >
            <i className="fas fa-cog"></i>
            <span className="text-sm font-medium">系统设置</span>
          </button>
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-orange-50 text-orange-700 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors"
            onClick={() => handleQuickAction('导出配置')}
          >
            <i className="fas fa-download"></i>
            <span className="text-sm font-medium">导出配置</span>
          </button>
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors"
            onClick={() => handleQuickAction('导入配置')}
          >
            <i className="fas fa-upload"></i>
            <span className="text-sm font-medium">导入配置</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Device;
